import React, { useEffect, useRef, useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { PROMO_CAROUSEL_IMAGES } from "@/data/products";
import StoreImg from "@/components/store/StoreImg";

/**
 * Premium 50% OFF banner with a 5-image carousel background.
 * - Auto-rotates every 4.5s
 * - Drag/swipe with momentum (touch + mouse)
 * - Keyboard arrow support on the carousel container
 * - Respects prefers-reduced-motion
 */
export default function PromoBanner() {
  const { t, isRTL } = useLanguage();
  const S = t.store;
  const images = PROMO_CAROUSEL_IMAGES;
  const n = images.length;

  const [index, setIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const dragStart = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % n), 4500);
    return () => clearInterval(id);
  }, [isPaused, n]);

  const onDown = (clientX) => {
    dragStart.current = clientX;
    setIsPaused(true);
  };

  const onMove = (clientX) => {
    if (dragStart.current == null) return;
    setDragOffset(clientX - dragStart.current);
  };

  const onUp = () => {
    if (dragStart.current == null) return;
    const w = containerRef.current?.clientWidth ?? 320;
    const threshold = Math.max(40, w * 0.18);
    if (dragOffset > threshold) {
      setIndex((i) => (i - 1 + n) % n);
    } else if (dragOffset < -threshold) {
      setIndex((i) => (i + 1) % n);
    }
    setDragOffset(0);
    dragStart.current = null;
    setTimeout(() => setIsPaused(false), 3500);
  };

  const next = () => {
    setIndex((i) => (i + 1) % n);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 4000);
  };

  const prev = () => {
    setIndex((i) => (i - 1 + n) % n);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 4000);
  };

  return (
    <section
      className="promo-banner-root relative overflow-hidden rounded-[28px] border border-[#F37021]/18 bg-[#0A0A0A] text-white md:rounded-[32px]"
      data-testid="store-promo-banner"
    >
      <div
        ref={containerRef}
        className="absolute inset-0 select-none cursor-grab active:cursor-grabbing"
        role="region"
        aria-label="Promo carousel"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") (isRTL ? prev() : next());
          if (e.key === "ArrowLeft") (isRTL ? next() : prev());
        }}
        onTouchStart={(e) => onDown(e.touches[0].clientX)}
        onTouchMove={(e) => onMove(e.touches[0].clientX)}
        onTouchEnd={onUp}
        onMouseDown={(e) => onDown(e.clientX)}
        onMouseMove={(e) => dragStart.current != null && onMove(e.clientX)}
        onMouseUp={onUp}
        onMouseLeave={() => dragStart.current != null && onUp()}
        data-testid="promo-carousel-track"
      >
        <div
          className="flex h-full w-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{
            transform: `translateX(calc(${-index * 100}% + ${dragOffset}px))`,
            transitionDuration: dragOffset !== 0 ? "0ms" : undefined,
          }}
        >
          {images.map((src, i) => (
            <div
              key={i}
              className="relative h-full w-full shrink-0"
              aria-hidden={i !== index}
              data-testid={`promo-slide-${i}`}
            >
              <StoreImg
                src={src}
                alt=""
                width={900}
                quality={70}
                draggable={false}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "low"}
                className="absolute inset-0 h-full w-full object-cover"
                style={{ filter: "brightness(0.44) saturate(1.08)" }}
              />
              <div
                className="absolute inset-0 h-full w-full"
                style={{
                  background:
                    "linear-gradient(120deg, rgba(10,10,10,0.62) 0%, rgba(10,10,10,0.32) 42%, rgba(10,10,10,0.78) 100%)",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0">
        <div className="promo-orb promo-orb--1" />
        <div className="promo-orb promo-orb--2" />
      </div>

      <div className="pointer-events-none relative flex flex-col gap-7 px-6 py-11 sm:px-10 sm:py-14 md:flex-row md:items-end md:justify-between md:px-14 md:py-16">
        <div className="max-w-3xl">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F8A567] backdrop-blur sm:text-xs">
            <Sparkles className="h-4 w-4 promo-sparkle" />
            {S.bannerEyebrow}
          </span>
          <div
            className="font-display font-black leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(48px, 12vw, 140px)", textShadow: "0 2px 30px rgba(0,0,0,0.5)" }}
            data-testid="banner-headline"
          >
            <span className="promo-headline">{S.bannerHeadline}</span>
          </div>
          <p
            className="mt-4 max-w-2xl text-lg font-medium text-white/92 sm:text-xl md:text-2xl"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
          >
            {S.bannerSub}
          </p>
        </div>

        <div
          className={`rounded-full border border-white/10 bg-black/18 px-4 py-2 text-[11px] uppercase tracking-[0.3em] text-white/70 backdrop-blur ${
            isRTL ? "text-left" : "text-right"
          }`}
        >
          {S.bannerFootnote}
        </div>
      </div>

      <div className="relative flex items-center justify-between gap-3 px-6 pb-5 sm:px-10 sm:pb-6 md:px-14">
        <div className="flex items-center gap-1.5" data-testid="promo-indicators">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setIndex(i);
                setIsPaused(true);
                setTimeout(() => setIsPaused(false), 4000);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-8 bg-[#F37021]" : "w-2.5 bg-white/24 hover:bg-white/45"
              }`}
              aria-label={`Slide ${i + 1}`}
              data-testid={`promo-indicator-${i}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={isRTL ? next : prev}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/16"
            aria-label="Previous"
            data-testid="promo-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={isRTL ? prev : next}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/16"
            aria-label="Next"
            data-testid="promo-next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <style>{`
        .promo-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(80px);
          will-change: transform;
        }
        .promo-orb--1 {
          width: 340px; height: 340px;
          background: #F37021;
          top: -110px; right: -70px;
          opacity: 0.18;
          animation: promo-float-a 18s ease-in-out infinite;
        }
        .promo-orb--2 {
          width: 240px; height: 240px;
          background: #FF8A3D;
          bottom: -80px; left: -50px;
          opacity: 0.16;
          animation: promo-float-b 22s ease-in-out infinite;
        }
        .promo-sparkle {
          animation: promo-pulse 4.5s ease-in-out infinite;
        }
        .promo-headline {
          color: #FFFFFF;
          background: linear-gradient(110deg, #FFFFFF 0%, #FFCC9E 40%, #F37021 60%, #FFFFFF 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: promo-shimmer 11s ease-in-out infinite;
          text-shadow: none;
        }
        @keyframes promo-float-a {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(-18px, 16px) scale(1.04); }
        }
        @keyframes promo-float-b {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(22px, -14px) scale(1.05); }
        }
        @keyframes promo-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.08); opacity: 0.82; }
        }
        @keyframes promo-shimmer {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .promo-orb, .promo-sparkle, .promo-headline { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
